import { namespace } from 'vuex-class';
import { IDealroomState } from '@/dealroom/store/dealroom.types';
import { EWindowSize } from '@/enums/window-size';
import { isWindowSize } from '@/helpers/window-size.helper';

export const dealroomNamespace = 'dealroom';

export const Dealroom = namespace(dealroomNamespace);

export const state: IDealroomState = {
  loading: false,
  loaded: false,

  document: undefined,
  currentDocumentPage: undefined,
  createDocumentId: undefined,
  pageToBeReplaced: null,

  selectedUser: null,
  selectedRecipient: { id: null, connectFirstRecipient: null },
  previewToken: {
    jwt: null,
    exp: null,
  },

  activeContentSelector: null,
  showAddRoleModal: false,
  showDocumentSettings: false,
  sidebarCollapsed: isWindowSize(EWindowSize.Small),

  sealValidationSuccess: null,
};

export enum EDealroomAction {
  LoadDocument = 'loadDocument',
  LoadDocumentSuccess = 'loadDocumentSuccess',
  LoadDocumentFailed = 'loadDocumentFailed',

  AddGoogleDocsPages = 'addGoogleDocsPages',
  AddDocument = 'addDocument',

  AddPages = 'addPages',
  RemovePage = 'removePage',
  UpdatePage = 'updatePage',

  ReplacePage = 'replacePage',
  CancelReplacePage = 'cancelReplacePage',
  ResetReplacePage = 'resetReplacePage',

  ShowEditDocumentPage = 'showEditDocumentPage',

  UpdateVideoPage = 'updateVideoPage',
  UpdateVideoPageFailed = 'updateVideoPageFailed',

  ResetState = 'resetState',
  ShowDocumentSettings = 'showDocumentSettings',

  UpdateDocument = 'updateDocument',
  UpdateDocumentFailed = 'updateDocumentFailed',

  AddDocumentTag = 'addDocumentTag',
  AddDocumentTagSuccess = 'addDocumentTagSuccess',
  AddDocumentTagFailed = 'addDocumentTagFailed',

  RestrictAccessAttachment = 'restrictAccessAttachment',
  RestrictAccessAttachmentFailed = 'restrictAccessAttachmentFailed',

  RemoveRestrictedAccessAttachment = 'removeRestrictedAccessAttachment',
  RemoveRestrictedAccessAttachmentFailed = 'removeRestrictedAccessAttachmentFailed',

  RequireViewAttachment = 'requireViewAttachment',
  RequireViewAttachmentFailed = 'requireViewAttachmentFailed',

  DeleteAttachment = 'deleteAttachment',
  DeleteAttachmentFailed = 'deleteAttachmentFailed',

  RemoveDocumentTag = 'removeDocumentTag',
  RemoveDocumentTagFailed = 'removeDocumentTagFailed',

  SetCurrentDocumentPage = 'setCurrentDocumentPage',

  SetSelectedUser = 'setSelectedUser',
  SetSelectedRecipient = 'setSelectedRecipient',

  RemoveRecipient = 'removeRecipient',
  RemoveRecipientSuccess = 'removeRecipientSuccess',
  RemoveRecipientFailed = 'removeRecipientFailed',

  CreateRecipient = 'createRecipient',
  CreateRecipientSuccess = 'createRecipientSuccess',
  CreateRecipientFailed = 'createRecipientFailed',

  UpdateRecipient = 'updateRecipient',
  UpdateRecipientFailed = 'updateRecipientFailed',
  TurnOnSigningOrder = 'turnOnSigningOrder',
  TurnOffSigningOrder = 'turnOffSigningOrder',
  UpdateRecipientApproversToCC = 'updateRecipientApproversToCC',
  LoadDocumentAttachments = 'loadDocumentAttachments',
  LoadDocumentAttachmentsSuccess = 'loadDocumentAttachmentsSuccess',
  LoadDocumentAttachmentsFailed = 'loadDocumentAttachmentsFailed',

  UpdateRecipientsSigningOrder = 'updateRecipientsSigningOrder',
  UpdateRecipientsSigningOrderFailed = 'updateRecipientsSigningOrderFailed',

  LoadRecipients = 'loadRecipients',
  LoadRecipientsSuccess = 'loadRecipientsSuccess',
  LoadRecipientsFailed = 'loadRecipientsFailed',

  LoadLegacyDocumentData = 'loadLegacyDocumentData',

  DeleteDocuments = 'deleteDocuments',
  DeleteDocumentsSuccess = 'deleteDocumentsSuccess',
  DeleteDocumentsFailed = 'deleteDocumentsFailed',

  DeleteTemplate = 'deleteTemplate',
  DeleteTemplateFailed = 'deleteTemplateFailed',

  UpdateDocumentUser = 'updateDocumentUser',
  UpdateDocumentUserFailed = 'updateDocumentUserFailed',
  ArrangeDocumentPages = 'arrangeDocumentPages',
  ArrangeDocumentPagesFailed = 'arrangeDocumentPagesFailed',

  SortSigningRecipients = 'sortSigningRecipients',

  ConnectFirstRecipientToFields = 'connectFirstRecipientToFields',

  GetPreviewToken = 'getPreviewToken',
  SetPages = 'setPages',

  UpdateRecipientMobile = 'updateRecipientMobile',
  UpdateRecipientMobileSuccess = 'updateRecipientMobileSuccess',

  LoadPageThumbUrl = 'loadPageThumbUrl',

  CreateDocument = 'createDocument',
  CreateDocumentSuccess = 'createDocumentSuccess',
  CreateDocumentFailed = 'createDocumentFailed',

  SealDocument = 'sealDocument',
  SealValidationFailed = 'sealValidationFailed',
  SealDocumentFailed = 'sealDocumentFailed',

  SetSidebarCollapsed = 'setSidebarCollapsed',
  SaveSignatureBase64 = 'saveSignatureBase64',
  SaveSignatureBase64Failed = 'saveSignatureBase64Failed',

  AssignFieldToRecipient = 'assignFieldToRecipient',
  AssignFieldToRecipientFailed = 'assignFieldToRecipientFailed',

  SetActiveContentSelector = 'setActiveContentSelector',

  AddEngageVideo = 'addEngageVideo',
  RemoveEngageVideo = 'removeEngageVideo',
  LoadDocumentResources = 'loadDocumentResources',
  LoadDocumentResourcesSuccess = 'loadDocumentResourcesSuccess',
  LoadDocumentResourcesFailed = 'loadDocumentResourcesFailed',

  DisconnectResource = 'disconnectResource',
  DisconnectResourceFailed = 'disconnectResourceFailed',

  ImplementResourceInDocument = 'implementResourceInDocument',
  ImplementResourceInDocumentFailed = 'implementResourceInDocumentFailed',

  ImplementResourceInTemplate = 'implementResourceInTemplate',
  ImplementResourceInTemplateFailed = 'implementResourceInTemplateFailed',

  ShowAddRoleModal = 'showAddRoleModal',

  AddRecipientRole = 'addRecipientRole',
  AddRecipientRoleSuccess = 'addRecipientRoleSuccess',
  AddRecipientRoleFailed = 'addRecipientRoleFailed',

  UpdateRecipientRole = 'updateRecipientRole',

  AddNewRecipient = 'addNewRecipient',

  RefreshRecipents = 'refreshRecipents',
  UpdateRecipientLegacy = 'updateRecipientLegacy',
  SetRecipientModalIsLoading = 'setRecipientModalIsLoading',
}

export enum EDealroomMutation {
  SetLoading = 'setLoading',
  SetLoaded = 'setLoaded',

  SetDocument = 'setDocument',
  SetPages = 'setPages',

  SetPageToBeReplaced = 'setPageToBeReplaced',

  ShowEditDocumentPage = 'showEditDocumentPage',
  SetState = 'setState',

  AddDocumentTag = 'addDocumentTag',
  RemoveDocumentTag = 'removeDocumentTag',

  SetCurrentDocumentPage = 'setCurrentDocumentPage',
  SetSelectedUser = 'setSelectedUser',
  SetSelectedRecipient = 'setSelectedRecipient',

  SetRecipients = 'setRecipients',
  AddRecipient = 'addRecipient',
  UpdateRecipient = 'updateRecipient',
  UpdateDocumentAttachment = 'updateDocumentAttachment',

  UpdateDocumentUser = 'updateDocumentUser',

  SetPreviewToken = 'setPreviewToken',
  ShowDocumentSettings = 'showDocumentSettings',

  SetCreateDocumentId = 'setCreateDocumentId',
  SetSealValidationSuccess = 'setSealValidationSuccess',
  SetSidebarCollapsed = 'setSidebarCollapsed',
  SetActiveContentSelector = 'setActiveContentSelector',
  SetEngageVideo = 'setEngageVideo',

  ShowAddRoleModal = 'showAddRoleModal',
}

export enum EDealroomGetter {
  FirstAddedRecipient = 'firstAddedRecipient',
  SelectedRecipient = 'selectedRecipient',
  Recipients = 'recipients',
  DocumentHasApprover = 'documentHasApprover',
  UploadingFile = 'uploadingFile',
  IsTemplate = 'isTemplate',
  ConnectFirstRecipient = 'connectFirstRecipient',
}
